import type { CampaignNotificationPref, MessageType } from "../../lib/types";
import { ALL_CAMPAIGNS } from "../../lib/types";
import { Icon } from "../ui/icon";
import { MessageTypeCheckboxes } from "./message-type-checkboxes";

interface CampaignPrefRowProps {
  pref: CampaignNotificationPref;
  /** Looked up by the parent from the live campaign list — a name, not just
   * an id, for a form the operator actually has to read. Falls back to the
   * id itself if the campaign has since vanished from the live list (e.g.
   * deleted in Aimfox) so an existing pref never disappears from view. */
  campaignName: string;
  campaignIsActive: boolean;
  showRequiredError: boolean;
  onChange: (messageTypes: MessageType[]) => void;
  onRemove: () => void;
}

// One row per campaign a recipient hears from — its own message-type
// selection, independent of every other row (per spec: "the message we
// choose... will be per campaign, not universal"). Composed from the two
// reusable pieces (CampaignMultiSelect adds these, MessageTypeCheckboxes
// drives each) rather than duplicating either.
export function CampaignPrefRow({
  pref, campaignName, campaignIsActive, showRequiredError, onChange, onRemove,
}: CampaignPrefRowProps) {
  return (
    <div className="bg-surface-container-low rounded-xl p-spacing-base space-y-spacing-xs">
      <div className="flex items-center justify-between gap-spacing-sm">
        <span className="font-label-md text-label-md text-on-surface flex items-center gap-spacing-2xs">
          {campaignName}
          {pref.campaignId !== ALL_CAMPAIGNS && campaignIsActive && (
            <span className="px-1.5 py-0.5 rounded-lg bg-[#ecfdf5] text-[#065f46] font-label-sm text-label-sm">
              active
            </span>
          )}
        </span>
        <button
          type="button"
          onClick={onRemove}
          className="text-on-surface-variant hover:text-error flex items-center gap-spacing-3xs font-label-sm text-label-sm"
          aria-label={`Remove ${campaignName}`}
        >
          <Icon name="close" className="text-[16px]" />
          Remove
        </button>
      </div>
      <MessageTypeCheckboxes
        selected={pref.messageTypes}
        onChange={onChange}
        showRequiredError={showRequiredError}
      />
    </div>
  );
}
